import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { usePWA } from '@/hooks/usePWA';

const UPDATE_TOAST_ID = 'pwa-update-available';
const OFFLINE_READY_TOAST_ID = 'pwa-offline-ready';

/**
 * Drives the Progressive Web App lifecycle for the web build:
 *  - registers the service worker (once, on mount)
 *  - prompts the user to reload when a new version is available
 *  - confirms when the app is ready to work offline
 *  - shows a persistent indicator while the device is offline
 */
const PWAManager = () => {
  const { isOnline } = usePWA();

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      console.error('Service worker registration failed:', error);
    },
  });

  useEffect(() => {
    if (!offlineReady) return;
    toast.success('Ready to work offline', {
      id: OFFLINE_READY_TOAST_ID,
      description: 'Star Chores is now available without an internet connection.',
    });
    setOfflineReady(false);
  }, [offlineReady, setOfflineReady]);

  useEffect(() => {
    if (!needRefresh) return;
    toast('A new version is available', {
      id: UPDATE_TOAST_ID,
      description: 'Reload to get the latest features and fixes.',
      duration: Infinity,
      action: {
        label: 'Reload',
        onClick: () => updateServiceWorker(true),
      },
      onDismiss: () => setNeedRefresh(false),
    });
  }, [needRefresh, setNeedRefresh, updateServiceWorker]);

  if (isOnline) return null;

  return (
    <div
      data-testid="pwa-offline-indicator"
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex justify-center px-4 pt-[calc(0.5rem+env(safe-area-inset-top,0px))]"
    >
      <span className="flex items-center gap-2 rounded-full bg-orange-500/95 px-4 py-2 text-sm font-medium text-white shadow-lg backdrop-blur">
        <WifiOff className="h-4 w-4" aria-hidden="true" />
        You&apos;re offline — changes are saved on this device
      </span>
    </div>
  );
};

export default PWAManager;
